import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wallet, TrendUp, ChartLine, ClockClockwise, ArrowsClockwise, Clock } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import PortfolioHolding from '../components/PortfolioHolding';
import PriceUpdateIndicator from '../components/PriceUpdateIndicator';
import TeenVestLoader from '../components/TeenVestLoader';
import { formatCurrency, formatPercent } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatTimeSince } from '../hooks/useRealTimeData';

const Portfolio = () => {
  const { user } = useAuth();
  const { format: fmtCcy } = useCurrency();
  const [portfolio, setPortfolio] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const intervalId = setInterval(() => {
      fetchData(true);
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setIsUpdating(true);
      
      const [portfolioRes, transactionsRes] = await Promise.all([
        api.get('/portfolio'),
        api.get('/portfolio/transactions')
      ]);
      setPortfolio(portfolioRes.data);
      setTransactions(transactionsRes.data.slice(0, 10));
      setLastUpdate(new Date());
    } catch (error) {
      if (!silent) {
        toast.error('Failed to load portfolio data');
      }
    } finally {
      setLoading(false);
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <TeenVestLoader size={112} label="Loading portfolio..." />
        </div>
      </div>
    );
  }

  const stats = [
    {
      icon: Wallet,
      label: 'Cash Balance',
      value: fmtCcy(user?.balance || 0),
      color: 'text-gold'
    },
    {
      icon: ChartLine,
      label: 'Investments Value',
      value: fmtCcy(portfolio?.summary?.total_value || 0),
      color: 'text-blue-400'
    },
    {
      icon: TrendUp,
      label: 'Total Profit/Loss',
      value: fmtCcy(portfolio?.summary?.total_profit_loss || 0),
      color: portfolio?.summary?.total_profit_loss >= 0 ? 'text-success' : 'text-destructive'
    },
    {
      icon: Wallet,
      label: 'Total Portfolio',
      value: fmtCcy(portfolio?.summary?.total_portfolio_value || 25000),
      color: 'text-white'
    }
  ];

  return (
    <div className="min-h-screen noise-bg" data-testid="portfolio-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white mb-2">
                Your Portfolio
              </h1>
              <div className="flex items-center gap-4">
                <p className="text-lg text-zinc-400">Track your investments and performance</p>
                <div className="flex items-center gap-2">
                  <motion.div
                    animate={{
                      scale: isUpdating ? [1, 1.2, 1] : 1,
                      opacity: isUpdating ? [1, 0.5, 1] : 1
                    }}
                    transition={{ duration: 1, repeat: isUpdating ? Infinity : 0 }}
                    className="w-2 h-2 rounded-full bg-success"
                  />
                  <span className="text-sm text-zinc-500">Live • Updates every minute</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <PriceUpdateIndicator isUpdating={isUpdating} lastUpdate={lastUpdate} />
              <button
                onClick={() => fetchData()}
                disabled={isUpdating}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                <ArrowsClockwise 
                  size={20} 
                  className={isUpdating ? 'animate-spin' : ''} 
                />
                <span className="text-sm text-white">Refresh</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6"
                data-testid={`portfolio-stat-${index}`}
              >
                <Icon size={32} weight="duotone" className={stat.color} />
                <p className="text-sm text-zinc-400 uppercase tracking-wide mt-4 mb-1">
                  {stat.label}
                </p>
                <p className={`text-2xl font-mono font-bold ${stat.color}`}>{stat.value}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Holdings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-unbounded font-bold text-white mb-6">Holdings</h2>
          {portfolio?.holdings && portfolio.holdings.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {portfolio.holdings.map((holding) => (
                <PortfolioHolding key={holding.symbol} holding={holding} />
              ))}
            </div>
          ) : (
            <div className="glass-card p-12 text-center">
              <p className="text-zinc-400 text-lg mb-4">You don't have any holdings yet</p>
              <p className="text-zinc-500">Start trading to build your portfolio!</p>
            </div>
          )}
        </motion.div>

        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-2xl font-unbounded font-bold text-white mb-6">Recent Transactions</h2>
          {transactions.length > 0 ? (
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="transactions-table">
                  <thead className="border-b border-white/10">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-zinc-400 uppercase tracking-wide">
                        Type
                      </th>
                      <th className="text-left p-4 text-sm font-medium text-zinc-400 uppercase tracking-wide">
                        Stock
                      </th>
                      <th className="text-right p-4 text-sm font-medium text-zinc-400 uppercase tracking-wide">
                        Shares
                      </th>
                      <th className="text-right p-4 text-sm font-medium text-zinc-400 uppercase tracking-wide">
                        Price
                      </th>
                      <th className="text-right p-4 text-sm font-medium text-zinc-400 uppercase tracking-wide">
                        Total
                      </th>
                      <th className="text-right p-4 text-sm font-medium text-zinc-400 uppercase tracking-wide">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction, index) => (
                      <tr
                        key={transaction.id}
                        className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
                        data-testid={`transaction-${index}`}
                      >
                        <td className="p-4">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase ${
                              transaction.type === 'buy'
                                ? 'bg-success/20 text-success'
                                : 'bg-destructive/20 text-destructive'
                            }`}
                          >
                            {transaction.type}
                          </span>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-bold text-white">{transaction.symbol}</p>
                            <p className="text-xs text-zinc-500">{transaction.company_name}</p>
                          </div>
                        </td>
                        <td className="p-4 text-right font-mono text-white">
                          {transaction.shares.toFixed(2)}
                        </td>
                        <td className="p-4 text-right font-mono text-white">
                          {fmtCcy(transaction.price)}
                        </td>
                        <td className="p-4 text-right font-mono font-bold text-white">
                          {fmtCcy(transaction.total)}
                        </td>
                        <td className="p-4 text-right text-sm text-zinc-400">
                          {new Date(transaction.timestamp).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="glass-card p-12 text-center">
              <ClockClockwise size={48} className="mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400 text-lg mb-2">No transactions yet</p>
              <p className="text-zinc-500">Your trading history will appear here</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Portfolio;